import { useEffect, useState } from "react";
import { api } from "./api";

/* ---------- offline capture queue (localStorage-backed) ---------- */
const QUEUE_KEY = "mai_offline_queue";
const QUEUE_EVENT = "mai-queue-change";
const MAX_AUDIO_B64 = 3_500_000; // ~2.5MB audio → base64

export const getQueue = () => {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
};
const setQueue = (q) => {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  window.dispatchEvent(new Event(QUEUE_EVENT));
};

export function queueCapture(item) {
  setQueue([...getQueue(), { ...item, id: crypto.randomUUID(), queued_at: new Date().toISOString() }]);
}
export const removeFromQueue = (id) => setQueue(getQueue().filter((i) => i.id !== id));

export const blobToB64 = (blob) =>
  new Promise((resolve, reject) => {
    if (blob.size > MAX_AUDIO_B64) return reject(new Error("too-large"));
    const r = new FileReader();
    r.onloadend = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

export function b64ToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(",");
  const mime = meta.match(/:(.*?);/)[1];
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export async function processQueueItem(item) {
  if (item.type === "voice") {
    const fd = new FormData();
    if (item.kol_id) fd.append("kol_id", item.kol_id);
    if (item.captured_tz) fd.append("captured_tz", item.captured_tz);
    fd.append("audio", b64ToBlob(item.audio_b64), "offline-note.webm");
    return api.voice(fd);
  }
  return api.transcript({ kol_id: item.kol_id || null, transcript: item.text, captured_tz: item.captured_tz || null });
}

/**
 * Sync queued captures. Items whose KOL the AI identifies confidently are
 * auto-confirmed; ambiguous ones stay queued for one-tap review in Capture.
 */
export async function syncQueue() {
  let synced = 0;
  for (const item of getQueue()) {
    try {
      const draft = await processQueueItem(item);
      if (draft.kol_id) {
        await api.confirm(draft.id, draft.structured, draft.kol_id);
        removeFromQueue(item.id);
        synced++;
      }
    } catch {
      /* stays queued; retried on next reconnect */
    }
  }
  return { synced, remaining: getQueue().length };
}

/* ---------- hooks ---------- */
export function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return online;
}

export function useQueueCount() {
  const [count, setCount] = useState(getQueue().length);
  useEffect(() => {
    const update = () => setCount(getQueue().length);
    window.addEventListener(QUEUE_EVENT, update);
    window.addEventListener("storage", update);
    return () => { window.removeEventListener(QUEUE_EVENT, update); window.removeEventListener("storage", update); };
  }, []);
  return count;
}
